import { action, computed, observable } from 'mobx'
import { Observable, Subject } from 'rxjs'
import { task } from 'mobx-task'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import type { Id } from '@/infra/id'
import { ModalViewModel } from '@/infra/ModalViewModel'

/**
 * A participant the note's author is permitted to notify.
 */
export interface JobNoteRecipient {
  userPublicId: Id
  name: string
  role: 'RM' | 'Pro'
}

/**
 * A notify recipient plus its current checkbox state.
 */
export interface NoteRecipientOption extends JobNoteRecipient {
  selected: boolean
}

// Mirrors the body limit from job note service.
export const MAX_NOTE_LENGTH = 2000

/**
 * Composes a note to a job, either in the add note dialog or in the notes
 * section's inline composer.
 */
export class AddJobNoteDialogViewModel {
  /**
   * Public IDs of the recipients the author has chosen to notify.
   */
  private readonly _selectedRecipientIds = observable.set<Id>()

  /**
   * Backing subject for `focusComposer$`.
   */
  private readonly _focusComposer = new Subject<void>()

  /**
   * Whether focus was asked for while no composer was mounted.
   */
  private _isFocusPending = false

  readonly modal = new ModalViewModel()

  @observable accessor composerText = ''

  /**
   * Emits when the inline composer should take focus, including once on
   * subscribing when focus was asked for before it mounted.
   */
  readonly focusComposer$ = new Observable<void>((subscriber) => {
    const subscription = this._focusComposer.subscribe(subscriber)
    if (this._isFocusPending) {
      this._isFocusPending = false
      subscriber.next()
    }
    return subscription
  })

  constructor(
    private readonly _createNote: (
      body: string,
      recipientUserPublicIds: Id[],
    ) => Promise<unknown>,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _getRecipients: () => JobNoteRecipient[],
  ) {}

  /**
   * Whether the note can be sent.
   */
  @computed
  get canSend() {
    return this.composerText.trim().length > 0 && !this.sendNote.pending
  }

  /**
   * The participants the author may notify, with their checkbox state.
   */
  @computed
  get recipients(): NoteRecipientOption[] {
    return this._getRecipients().map((recipient) => ({
      ...recipient,
      selected: this._selectedRecipientIds.has(recipient.userPublicId),
    }))
  }

  /**
   * Characters remaining before the note hits the max length.
   */
  @computed
  get noteCharsRemaining() {
    return this.composerText
      ? MAX_NOTE_LENGTH - this.composerText.length
      : MAX_NOTE_LENGTH
  }

  /**
   * Shows the dialog with a fresh composer.
   */
  @action.bound
  show() {
    if (this.modal.showing) {
      return
    }

    this._reset()
    this.modal.show()
  }

  /**
   * Updates the note being written.
   */
  @action.bound
  setComposerText(text: string) {
    this.composerText = text
  }

  /**
   * Asks the inline composer to take focus.
   */
  @action.bound
  focusComposer() {
    if (this._focusComposer.observed) {
      this._focusComposer.next()
    } else {
      this._isFocusPending = true
    }
  }

  /**
   * Selects or deselects a recipient to notify.
   */
  @action.bound
  toggleRecipient(userPublicId: Id, selected: boolean) {
    if (selected) {
      this._selectedRecipientIds.add(userPublicId)
    } else {
      this._selectedRecipientIds.delete(userPublicId)
    }
  }

  /**
   * Creates a note, then clears the composer and closes the dialog if it is
   * showing.
   */
  sendNote = task.resolved(async () => {
    const text = this.composerText.trim()
    if (!text) {
      return
    }

    await this._createNote(text, [...this._selectedRecipientIds]).catch(
      this._createAlertErrorHandler('Adding the note failed'),
    )
    this._reset()
    this.modal.close()
  })

  @action
  private _reset() {
    this.composerText = ''
    this._selectedRecipientIds.clear()
  }
}
